import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  CITY,
  EMAIL,
  GEOGRAPHICAL_REGION,
  MOBILE,
  NAME,
  ROLE,
  STATE,
  STATUS
} from "../consts";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useEffect, useState, useRef } from "react";
import { stateList } from "@/consts";
import { useGeoRegionsQuery } from "@/services/masterData";
import { nan } from "zod";

const useFilter = ({
  filterData,
  setFilterData,
  onCancel,
}: {
  filterData: any;
  setFilterData: React.Dispatch<React.SetStateAction<any>>;
  onCancel: () => void;
}) => {
  const isMobile = useIsMobile();
  const [selectedState, setSelectedState] = useState("");
  const initializedFromDetailRef = useRef(false);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [EMAIL]: "",
      [MOBILE]: "",
      [ROLE]: "",
      [GEOGRAPHICAL_REGION]: "",
      [STATE]: "",
      [CITY]: "",
      [STATUS]: "",
    },
  });

  useEffect(() => {
    if (filterData) {
      initializedFromDetailRef.current = true;
      form.reset({
        [NAME]: filterData?.[NAME] || "",
        [EMAIL]: filterData?.[EMAIL] || "",
        [MOBILE]: filterData?.[MOBILE] || "",
        [ROLE]: filterData?.[ROLE] || "",
        [GEOGRAPHICAL_REGION]: filterData?.[GEOGRAPHICAL_REGION] || "",
        [STATE]: filterData?.[STATE] || "",
        [CITY]: filterData?.[CITY] || "",
      });
    }
  }, [filterData, form]);

  // Fetch geographical regions
  const {
    data: geoRegionsData,
    isLoading: isLoadingGeoRegions,
    error: geoRegionsError,
  } = useGeoRegionsQuery({}, { enabled: true });

  const requiredFields = getRequiredFields(schema);
  const geographicalRegionOptions =
    geoRegionsData?.data?.[0]?.records?.map((region: any) => ({
      label: region.name,
      value: region.id,
    })) || [];

  const stateOptions = Object.keys(stateList).map((state) => ({
    label: state,
    value: state,
  }));

  const cityOptions = selectedState
    ? (stateList[selectedState as keyof typeof stateList] || []).map(
        (city) => ({
          label: city,
          value: city,
        })
      )
    : [];

  const watchedState = form.watch(STATE);

  useEffect(() => {
    // During initial detail load, sync selectedState without clearing CITY
    if (initializedFromDetailRef.current) {
      setSelectedState(watchedState || "");
      initializedFromDetailRef.current = false;
      return;
    }
    if (watchedState !== selectedState) {
      setSelectedState(watchedState || "");
      // Reset city when user changes state
      form.setValue(CITY, "");
    }
  }, [watchedState, selectedState, form]);

  const onSubmit = (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(data);
    setFilterData(_data);
    onCancel();
  };

  const onClear = () => {
  form.reset({
    [NAME]: "",
    [EMAIL]: "",
    [MOBILE]: "",
    [ROLE]: "",
    [GEOGRAPHICAL_REGION]: "",
    [STATE]: "",
    [CITY]: "",
  });
  setFilterData(null); // Optional, if you want to clear applied filters
};


  return {
    form,
    onSubmit,
    requiredFields,
    isMobile,
    onClear,
    geographicalRegionOptions,
    stateOptions,
    cityOptions,
  };
};

export default useFilter;
